import {BreadCrumb, Dictionary, IndustrySegmentsTaxonomyTerm} from "@/types/hygiena-types";
import {MutableRefObject, useContext, useEffect, useState} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {buildBreadCrumbs} from "@/lib/breadcrumbs";
import {MediaImage} from "@/components/templates/media/media--image";
import {HERO_IMAGE_STYLE} from "@/types/image-styles";
import {HERO_IMAGE_SIZES, HERO_MAX_WIDTH, PlaceHolderHeroImage} from "../hero";
import {FrostedGlassContainer} from "@/components/atoms/gradient-container";
import {Link} from "@/components/atoms/link";
import {Body} from "@/components/atoms/body";
import {FormattedText} from "@/components/formatted-text";
import {Button} from "@/components/atoms/button";
import {getDocumentUri} from "@/lib/get-document-uri";
import {absoluteURL} from "@/lib/absolute-url";
import {CONTACT_PORTAL_ID} from "@/lib/constants";

/**
 * Industry/Segment Taxonomy Page Hero.
 * @param taxonomy_term
 *   A Product Type/Product Name taxonomy term.
 * @param scrollRef
 *   A ref to the section to scroll to on button click.
 * @constructor
 */
export function IndustrySegmentHero ({
                                       taxonomy_term,
                                       scrollRef
                                     }: {
  taxonomy_term: IndustrySegmentsTaxonomyTerm,
  scrollRef?: MutableRefObject<any>
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const [smallScreen, setSmallScreen] = useState<boolean>();
  const [beginVideo, setBeginVideo] = useState<boolean>(false);
  const video = taxonomy_term?.field_hero_video;
  const image = taxonomy_term?.field_industry_hero_image;
  const breadcrumbs = [] as BreadCrumb[];
  const heroButtonText = taxonomy_term?.field_industry_hero_seg_btn_text
    ?? t?.all?.see_industry_testing_solutions?.replace("{name}", taxonomy_term.name)
    ?? `${t?.all?.see} ${taxonomy_term.name} ${t?.all?.testing_solutions}`;

  // We use the video URL if there is only one video being passed, if the video is an array, we
  // apply a fader.
  let videoFile;
  if (Array.isArray(video) && video?.length === 1) {
    videoFile = video?.[0]?.field_media_file;
  }
  else if (!Array.isArray(video) && video?.field_media_file) {
    videoFile = video.field_media_file;
  }
  const videoUrl = videoFile?.uri?.url
    ? absoluteURL(videoFile?.uri?.url)
    : undefined;

  function jumpToContactus() {
    const form = document.getElementById(CONTACT_PORTAL_ID);
    if (form) {
      form.scrollIntoView({behavior: 'smooth'})
    }
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const setSize = () => setSmallScreen(window.innerWidth < 768);
    setSize();
    const loadVideo = () => !beginVideo && setBeginVideo(true);
    window.addEventListener("resize", setSize);
    window.addEventListener("mousemove", loadVideo);

    return () => {
      window.removeEventListener("resize", setSize);
      window.removeEventListener("mousemove", loadVideo);
    }
  }, []);

  /**
   * Scrolls to the solutions title if present.
   */
  function scrollToRef() {
    if (!scrollRef?.current) return;
    const current = scrollRef.current as HTMLHeadingElement|HTMLDivElement;
    current.scrollIntoView({behavior: 'smooth'})
  }

  if (taxonomy_term?.parent) {
    buildBreadCrumbs(taxonomy_term?.parent[0], breadcrumbs);
  }

  return (
    <div className={"relative 2xl:mx-auto flex mx-auto py-12 max-w-[1920px] overflow-hidden md:py-0 md:h-[480px] lg:h-[720px]"}>
        {!videoUrl && image ? (
          <MediaImage
            priority
            loading={"eager"}
            media={image}
            imageStyle={HERO_IMAGE_STYLE}
            sizes={HERO_IMAGE_SIZES}
            key={`industry-segment-hero-image--${image.id}`}
            className={"absolute inset-0"}
            style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
        ) : (
          <PlaceHolderHeroImage altText={taxonomy_term?.name}/>
        )}
        {videoUrl && (
          <div className="h-full w-full absolute">
            {(image?.image || image?.bf_image) && (
              <MediaImage
                priority
                loading={"eager"}
                imageStyle={HERO_IMAGE_STYLE}
                media={image}
                key={`hero-image--${image.id}`}
                sizes={HERO_IMAGE_SIZES}
                className={"absolute inset-0 overflow-hidden"} style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
            )}
            {beginVideo && !smallScreen && (
              <video muted loop autoPlay className="max-md:hidden object-cover h-full w-full absolute">
                <source src={videoUrl} type="video/mp4"/>
              </video>
            )}
          </div>
        )}
      <div className="container mx-0 md:mx-8 lg:mx-auto py-12 md:py-0 min-h-fit flex lg:!justify-start items-center">
          <FrostedGlassContainer
            className="flex flex-col rounded-none md:rounded-lg p-8 md:p-12 lg:p-16 w-full lg:!max-w-4xl">
            <div className="text-primary font-semibold mb-4">
              {breadcrumbs.map((item, index) => {
                if (item.url) {
                  return (
                    <div className="inline" key={`breadcrumb--${item.id}`}>
                      <Link href={item.url ?? "/"} className="hover:text-white transition-all">{item.name}</Link>
                      {index + 1 < breadcrumbs.length && (<span className="px-1.5">/</span>)}
                    </div>
                  )
                }
              })}
            </div>
            <h1>{taxonomy_term?.field_industry_hero_title ?? taxonomy_term.name}</h1>
            <Body bodyType="lg" className="mb-8 md:mb-12 font-light tracking-snug leading-8 text-black">
              {taxonomy_term?.field_industry_hero_text?.processed && (
                <FormattedText processed={taxonomy_term.field_industry_hero_text.processed}/>
              )}
            </Body>
            <div className="flex flex-row gap-2 flex-wrap">
              {/* @todo Remember to translate all of these button texts */}
              {taxonomy_term?.field_industry_segment_brochure?.field_media_file?.uri?.url && (
                <Button
                  href={getDocumentUri(taxonomy_term?.field_industry_segment_brochure) ?? "/"}
                  height={20} width={20}
                  className="max-md:w-full btn-industry-brochure"
                  containerClassName="max-md:w-full"
                  target={"_blank"} hover={{color: "primary"}}
                  icon={"file"}>{taxonomy_term?.field_industry_brochure_btn_text ?? `${t?.all?.industry_brochure}`}</Button>
              )}
              {taxonomy_term?.field_industry_overview?.field_media_file?.uri?.url && (
                <Button
                  href={getDocumentUri(taxonomy_term?.field_industry_overview) ?? "/"}
                  height={20} width={20}
                  className="max-md:w-full btn-industry-overview"
                  containerClassName="max-md:w-full"
                  target={"_blank"} hover={{color: "primary"}}
                  icon={"file"}>{taxonomy_term?.field_industry_overview_btn_text ?? `${t?.all?.industry_overview}`}</Button>
              )}
              {scrollRef && (
                <Button
                  className="max-md:w-full btn-see-industry-solutions"
                  height={20} width={20}
                  containerClassName="max-md:w-full"
                  callback={scrollToRef} hover={{color: "primary"}} position={"right"}
                  icon={"arrow-down"}>{heroButtonText}</Button>
              )}
            </div>
            <div className="mt-3">
              <Button callback={jumpToContactus} className="py-1.5 px-1 bg-white w-full h-full" color={"primary"} hover={{color: "primary"}} invert={true}>{`${t?.all?.request_more_info}`}</Button>
            </div>
          </FrostedGlassContainer>
        </div>
      </div>
      );
      }

